<template>
  <div class="relative flex min-h-[6.5rem] min-w-0 max-w-full flex-1 flex-col items-stretch overflow-visible">
    <!-- Month label -->
    <div class="relative z-[1] mb-1 flex justify-center">
      <span
        class="inline-block rounded-md border border-zinc-200/90 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-600 shadow-sm sm:text-xs"
      >
        {{ getMonthShortName() }}
      </span>
    </div>

    <!-- Axis line (thin rail) -->
    <div
      class="pointer-events-none absolute left-0 right-0 top-1/2 z-0 h-px -translate-y-1/2 rounded-full bg-zinc-800/85 sm:bg-zinc-800/90"
      aria-hidden="true"
    />

    <!-- Today marker (current month only): label + line in-column (stays inside column; no horizontal scroll on parent row) -->
    <div
      v-if="isCurrentMonth"
      class="pointer-events-none absolute z-[8] flex -translate-x-1/2 flex-col items-center"
      :style="{ left: `${highlightPosition}%`, top: '18%', height: '70%' }"
    >
      <span
        class="mb-1 shrink-0 whitespace-nowrap rounded-md bg-gradient-to-br from-pink-500 to-[var(--color-orange)] px-2 py-0.5 text-[10px] font-bold text-white shadow-md sm:text-xs"
      >
        {{ $t('PersonalJourney.Timeline.today') }}
      </span>
      <div
        class="min-h-0 w-0.5 flex-1 rounded-full bg-gradient-to-b from-pink-500 via-rose-400 to-[var(--color-orange)] shadow-sm"
        aria-hidden="true"
      />
    </div>

    <!-- Tick at month start -->
    <div
      class="pointer-events-none absolute left-0 top-1/2 z-[1] h-2.5 w-px -translate-y-1/2 bg-zinc-800 sm:h-3"
      aria-hidden="true"
    />

    <!-- Event markers -->
    <div
      v-for="event in computedEvents"
      :key="event.date + event.type"
      class="event-icon-wrapper absolute z-[5] flex flex-col items-center mt-2"
      :style="{
        left: `${event.position}%`,
        top: `calc(52% + ${Math.floor(event.yPosition) * 30}px)`,
      }"
    >
      <div
        class="relative flex flex-col items-center"
        @mouseenter="onEventHover($event, event)"
        @mouseleave="onEventLeave"
      >
        <div
          class="relative inline-flex cursor-default items-center justify-center leading-none transition-transform hover:scale-110"
        >
          <template v-if="event.type === 'level'">
            <i
              :class="[
                getPrimeIcon(event.type),
                iconToneClass(event.date, event.type),
                '!text-2xl sm:!text-3xl',
              ]"
              aria-hidden="true"
            />
            <span
              v-if="event.level != null && event.level !== ''"
              class="pointer-events-none absolute left-1/2 top-[52%] -translate-x-1/2 -translate-y-1/2 font-extrabold tabular-nums tracking-tight text-white !text-sm sm:!text-base"
            >
              {{ event.level }}
            </span>
          </template>
          <template v-else>
            <i
              :class="[
                getPrimeIcon(event.type),
                iconToneClass(event.date, event.type),
                '!text-xl sm:!text-2xl',
              ]"
              aria-hidden="true"
            />
          </template>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <Transition name="tooltip-fade">
        <div
          v-if="hoveredEvent && tooltipRect"
          class="fixed z-[10000] max-w-[200px] rounded-lg border border-zinc-200 bg-white p-3 text-sm text-[var(--color-black)] shadow-lg"
          :style="tooltipStyle"
        >
          <template v-if="hoveredEvent.type === 'personal-goal'">
            <p v-if="isDatePast(hoveredEvent.date)" class="m-0">
              {{ $t('PersonalJourney.Timeline.personalGoalPast', { date: formatDate(hoveredEvent.date) }) }}
            </p>
            <p v-else class="m-0">
              {{ $t('PersonalJourney.Timeline.personalGoalFuture', { date: formatDate(hoveredEvent.date) }) }}
            </p>
          </template>
          <template v-else-if="hoveredEvent.type === 'workshop'">
            <p v-if="isDatePast(hoveredEvent.date)" class="m-0">
              {{ $t('PersonalJourney.Timeline.workshopPast', { date: formatDate(hoveredEvent.date) }) }}
            </p>
            <p v-else class="m-0">
              {{ $t('PersonalJourney.Timeline.workshopFuture', { date: formatDate(hoveredEvent.date) }) }}
            </p>
          </template>
          <template v-else-if="hoveredEvent.type === 'level'">
            {{ $t('PersonalJourney.Timeline.levelUp', { date: formatDate(hoveredEvent.date) }) }}
          </template>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'PersonalJourneyOneMonthTimelineComponent',
  props: {
    month: {
      type: Number,
      required: true,
    },
    year: {
      type: Number,
      required: true,
    },
    events: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      dayToHighlight: new Date().getDate(),
      hoveredEvent: null,
      tooltipRect: null,
    };
  },
  computed: {
    highlightPosition() {
      const daysInMonth = new Date(this.year, this.month + 1, 0).getDate();
      return (this.dayToHighlight / daysInMonth) * 100;
    },
    isCurrentMonth() {
      const today = new Date();
      return today.getMonth() === this.month && today.getFullYear() === this.year;
    },
    computedEvents() {
      const daysInMonth = new Date(this.year, this.month + 1, 0).getDate();
      const events = this.events.map((event) => ({
        ...event,
        position: (new Date(event.date).getDate() / daysInMonth) * 100,
        yPosition: 0,
      }));

      events.sort((a, b) => a.position - b.position);

      const highlightPos = this.highlightPosition;
      const overlapThreshold = 22;

      for (let i = 0; i < events.length; i += 1) {
        const eventDay = new Date(events[i].date).getDate();
        if (this.dayToHighlight > eventDay) {
          const highlightPosDifference = Math.abs(events[i].position - highlightPos);
          if (highlightPosDifference < overlapThreshold) {
            events[i].position = highlightPos - overlapThreshold - 1;
          }
        }

        for (let j = 0; j < i; j += 1) {
          if (events[i].position - events[j].position < overlapThreshold) {
            events[i].yPosition = Math.max(events[i].yPosition, events[j].yPosition + 1);
          }
        }
      }

      const yMax = events.length ? Math.max(...events.map((item) => item.yPosition)) : 0;
      this.$emit('updateMaxYPos', yMax);
      return events;
    },
    lang() {
      return this.$i18n.locale;
    },
    tooltipStyle() {
      if (!this.tooltipRect) return {};
      const gap = 8;
      return {
        left: `${this.tooltipRect.left + this.tooltipRect.width / 2}px`,
        bottom: `${window.innerHeight - this.tooltipRect.top + gap}px`,
        transform: 'translate(-50%, 0)',
      };
    },
  },
  methods: {
    onEventHover(domEvent, event) {
      if (event.type === 'new-feature') return;
      const el = domEvent.currentTarget;
      const rect = el.getBoundingClientRect();
      this.hoveredEvent = event;
      this.tooltipRect = rect;
    },
    onEventLeave() {
      this.hoveredEvent = null;
      this.tooltipRect = null;
    },
    getPrimeIcon(type) {
      switch (type) {
        case 'personal-goal':
          return 'pi pi-flag-fill';
        case 'workshop':
          return 'pi pi-users';
        case 'new-feature':
          return 'pi pi-sparkles';
        case 'level':
          return 'pi pi-star-fill';
        default:
          return 'pi pi-circle-fill';
      }
    },
    iconToneClass(date, type) {
      // Level milestones on this roadmap are projected next steps — same styling as upcoming items.
      if (type === 'level') {
        return 'text-zinc-800';
      }
      const eventDate = new Date(date);
      const today = new Date();
      const threeMonthsFromNow = new Date(today);
      threeMonthsFromNow.setMonth(today.getMonth() + 3);

      if (this.isDatePast(date)) {
        return 'text-[var(--color-orange)]';
      }
      if (eventDate <= threeMonthsFromNow) {
        return 'text-zinc-800';
      }
      return 'text-zinc-400';
    },
    isDatePast(dateToCheck) {
      const checkDate = new Date(dateToCheck);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return checkDate <= today;
    },
    formatDate(originalDate) {
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'CET',
        hour12: false,
      };
      const formattedDate = new Date(originalDate).toLocaleString(this.lang, options);
      if (formattedDate === 'Invalid Date') {
        return ' ';
      }
      return formattedDate;
    },
    getMonthShortName() {
      const date = new Date(this.year, this.month, 1);
      return date.toLocaleString(this.lang, { month: 'short' });
    },
  },
};
</script>

<style scoped>
.event-icon-wrapper {
  width: 22%;
  transform: translateX(-50%);
}

.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.15s ease;
}
.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
}
</style>
